import { z } from "zod";

export const paymentScenarioSchema = z.enum([
  "wx_native", "wx_jsapi", "wx_app", "wx_mini_program", "wx_h5", "wx_code", "wx_face_to_face",
  "wx_combine_native", "wx_combine_jsapi", "wx_combine_app", "wx_combine_h5", "wx_combine_mini_program",
  "wx_refund", "wx_profit_sharing", "wx_bill", "wx_transfer", "wx_pay_score", "wx_coupon", "wx_red_packet", "wx_complaint", "wx_customs",
  "alipay_page", "alipay_wap", "alipay_app", "alipay_mini_program", "alipay_face_to_face",
  "alipay_refund", "alipay_profit_sharing", "alipay_bill", "alipay_transfer", "alipay_batch_transfer", "alipay_withdraw", "alipay_pre_auth", "alipay_deduct", "alipay_red_packet", "alipay_marketing",
  "apple_iap",
]);
export type PaymentScenario = z.infer<typeof paymentScenarioSchema>;

export const paymentCredentialRefSchema = z.string().trim().regex(/^[A-Z][A-Z0-9_]{2,127}$/);
export const paymentCredentialRefsSchema = z.record(z.string().min(1).max(64), paymentCredentialRefSchema).optional();
export type PaymentCredentialRefs = z.infer<typeof paymentCredentialRefsSchema>;
export const paymentCredentialValuesSchema = z.record(z.string().min(1).max(64), z.string().min(1).max(32_768)).optional();

export const paymentProtocolConfigSchema = z.object({
  wxpayMerchantType: z.enum(["ordinary", "service_provider"]).optional(),
  wxpayVerificationMode: z.enum(["public_key", "platform_certificate"]).optional(),
  alipaySignAlgorithm: z.literal("RSA2").optional(),
  alipayKeyMode: z.enum(["certificate", "public_key"]).optional(),
}).strict().default({});
export type PaymentProtocolConfig = z.infer<typeof paymentProtocolConfigSchema>;

export const paymentConfigSchema = z.object({
  id: z.string().min(1).max(128),
  name: z.string().trim().min(2).max(64),
  channel: z.enum(["wxpay", "alipay", "apple"]),
  scenarios: z.array(paymentScenarioSchema).min(1),
  accountId: z.string().trim().min(1).max(128),
  secretRef: z.string().trim().regex(/^[A-Z][A-Z0-9_]{2,127}$/),
  credentialRefs: paymentCredentialRefsSchema,
  credentialStatus: z.record(z.string().min(1).max(64), z.boolean()).optional(),
  protocolConfig: paymentProtocolConfigSchema,
  notifyUrl: z.string().trim().url().refine((value) => value.startsWith("https://"), "回调 URL 必须使用 HTTPS").optional(),
  description: z.string().trim().max(200),
  enabled: z.boolean(),
  updatedAt: z.string().datetime(),
  associatedApps: z.array(z.object({ id: z.string(), name: z.string(), code: z.string() })).default([]),
});
export type PaymentConfig = z.infer<typeof paymentConfigSchema>;

export const upsertPaymentConfigInputSchema = paymentConfigSchema
  .omit({ id: true, updatedAt: true, associatedApps: true, credentialStatus: true })
  .extend({ credentialValues: paymentCredentialValuesSchema });
export type UpsertPaymentConfigInput = z.infer<typeof upsertPaymentConfigInputSchema>;
import { payChannelSchema } from "../m1-app";

export const paymentOrderStatusSchema = z.enum([
  "pending",
  "processing",
  "succeeded",
  "failed",
  "canceled",
  "refunded",
]);
export type PaymentOrderStatus = z.infer<typeof paymentOrderStatusSchema>;

export const createPaymentOrderInputSchema = z.object({
  idempotencyKey: z.string().trim().min(8).max(128),
  channel: payChannelSchema,
  amountMinor: z.number().int().min(1).max(1_000_000_000),
  currency: z.string().trim().regex(/^[A-Z]{3}$/).default("CNY"),
  description: z.string().trim().min(1).max(256),
  metadata: z.record(z.string(), z.unknown()).default({}),
}).strict();
export type CreatePaymentOrderInput = z.infer<typeof createPaymentOrderInputSchema>;

export const paymentOrderSchema = z.object({
  id: z.string().min(1).max(128),
  appId: z.string().min(1).max(128),
  userId: z.string().min(1).max(128),
  idempotencyKey: z.string().min(8).max(128),
  channel: payChannelSchema,
  amountMinor: z.number().int().positive(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  description: z.string().max(256),
  status: paymentOrderStatusSchema,
  providerOrderId: z.string().max(256).nullable(),
  metadata: z.record(z.string(), z.unknown()),
  failureCode: z.string().max(128).nullable(),
  failureReason: z.string().max(500).nullable(),
  version: z.number().int().positive(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  paidAt: z.string().datetime().nullable(),
});
export type PaymentOrder = z.infer<typeof paymentOrderSchema>;

export const paymentRefundStatusSchema = z.enum([
  "processing",
  "succeeded",
  "failed",
  "canceled",
]);
export type PaymentRefundStatus = z.infer<typeof paymentRefundStatusSchema>;

export const createPaymentRefundInputSchema = z.object({
  idempotencyKey: z.string().trim().min(8).max(128),
  amountMinor: z.number().int().min(1).max(1_000_000_000),
  reason: z.string().trim().min(1).max(256),
}).strict();
export type CreatePaymentRefundInput = z.infer<typeof createPaymentRefundInputSchema>;

export const paymentRefundSchema = z.object({
  id: z.string().min(1).max(128),
  orderId: z.string().min(1).max(128),
  appId: z.string().min(1).max(128),
  idempotencyKey: z.string().min(8).max(128),
  outRefundNo: z.string().min(1).max(128),
  providerRefundId: z.string().max(256).nullable(),
  amountMinor: z.number().int().positive(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  reason: z.string().max(256),
  status: paymentRefundStatusSchema,
  failureReason: z.string().max(500).nullable(),
  version: z.number().int().positive(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
});
export type PaymentRefund = z.infer<typeof paymentRefundSchema>;

export const wechatTradeStateSchema = z.enum([
  "SUCCESS",
  "REFUND",
  "NOTPAY",
  "CLOSED",
  "REVOKED",
  "USERPAYING",
  "PAYERROR",
  "ACCEPT",
]);
export type WechatTradeState = z.infer<typeof wechatTradeStateSchema>;

export const adminPaymentOrderSchema = z.object({
  id: z.string().min(1).max(128),
  appId: z.string().min(1).max(128),
  appName: z.string().min(1),
  appCode: z.string().min(1),
  userId: z.string().min(1).max(128),
  userPublicId: z.string().regex(/^\d+$/),
  userName: z.string().min(1),
  userAvatarUrl: z.union([
    z.literal("/open/user/avatar"),
    z.string().regex(/^https:\/\//).max(500),
    z.null(),
  ]).optional(),
  channel: payChannelSchema,
  amountMinor: z.number().int().positive(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  description: z.string().max(256),
  status: paymentOrderStatusSchema,
  providerOrderId: z.string().max(256).nullable(),
  failureCode: z.string().max(128).nullable(),
  failureReason: z.string().max(500).nullable(),
  attemptStatus: z.enum(["creating", "ready", "failed", "expired"]).nullable(),
  attemptExpiresAt: z.string().datetime().nullable(),
  latestTradeState: wechatTradeStateSchema.nullable(),
  latestNotificationStatus: z.enum(["received", "processed", "ignored", "rejected"]).nullable(),
  latestRejectionReason: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  paidAt: z.string().datetime().nullable(),
});
export type AdminPaymentOrder = z.infer<typeof adminPaymentOrderSchema>;

export const adminPaymentOrderListQuerySchema = z.object({
  keyword: z.string().trim().max(100).default(""),
  status: z.union([paymentOrderStatusSchema, z.literal("all")]).default("all"),
  appId: z.string().trim().max(128).default(""),
  channel: z.union([payChannelSchema, z.literal("all")]).default("all"),
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
});
export type AdminPaymentOrderListQuery = z.infer<typeof adminPaymentOrderListQuerySchema>;

export const adminPaymentOrderListResultSchema = z.object({
  items: z.array(adminPaymentOrderSchema),
  total: z.number().int().nonnegative(),
  summary: z.object({
    total: z.number().int().nonnegative(),
    processing: z.number().int().nonnegative(),
    succeeded: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
  }),
});
export type AdminPaymentOrderListResult = z.infer<typeof adminPaymentOrderListResultSchema>;

export const adminPaymentOrderDetailSchema = z.object({
  order: adminPaymentOrderSchema.omit({
    attemptStatus: true,
    attemptExpiresAt: true,
    latestTradeState: true,
    latestNotificationStatus: true,
    latestRejectionReason: true,
  }).extend({
    metadata: z.record(z.string(), z.unknown()),
    version: z.number().int().positive(),
  }),
  attempt: z.object({
    status: z.enum(["creating", "ready", "failed", "expired"]),
    hasCodeUrl: z.boolean(),
    expiresAt: z.string().datetime().nullable(),
    providerErrorCode: z.string().nullable(),
    providerErrorReason: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }).nullable(),
  notifications: z.array(z.object({
    id: z.string(),
    source: z.enum(["callback", "query"]),
    tradeState: wechatTradeStateSchema,
    tradeType: z.string().nullable(),
    amountMinor: z.number().int().nullable(),
    currency: z.string().nullable(),
    status: z.enum(["received", "processed", "ignored", "rejected"]),
    rejectionReason: z.string().nullable(),
    occurredAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    processedAt: z.string().datetime().nullable(),
  })),
  refunds: z.array(paymentRefundSchema).default([]),
});
export type AdminPaymentOrderDetail = z.infer<typeof adminPaymentOrderDetailSchema>;

export const paymentReconciliationQuerySchema = z.object({
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  channel: z.union([payChannelSchema, z.literal("all")]).default("all"),
}).superRefine((value, context) => {
  if (value.dateFrom > value.dateTo) context.addIssue({ code: "custom", path: ["dateTo"], message: "结束日期不能早于开始日期" });
});
export type PaymentReconciliationQuery = z.infer<typeof paymentReconciliationQuerySchema>;

export const paymentReconciliationBatchSchema = z.object({
  id: z.string().min(1).max(128),
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  channel: z.union([payChannelSchema, z.literal("all")]),
  status: z.enum(["completed", "attention", "running", "failed"]),
  generatedAt: z.string().datetime(),
  summary: z.object({
    orderCount: z.number().int().nonnegative(),
    grossAmountMinor: z.number().int().nonnegative(),
    refundedAmountMinor: z.number().int().nonnegative(),
    netAmountMinor: z.number().int().nonnegative(),
    mismatchCount: z.number().int().nonnegative(),
  }),
  rows: z.array(z.object({
    channel: payChannelSchema,
    orderCount: z.number().int().nonnegative(),
    grossAmountMinor: z.number().int().nonnegative(),
    refundedAmountMinor: z.number().int().nonnegative(),
    netAmountMinor: z.number().int().nonnegative(),
    mismatchCount: z.number().int().nonnegative(),
  })),
});
export type PaymentReconciliationBatch = z.infer<typeof paymentReconciliationBatchSchema>;

export const paymentStatementChannelSchema = z.enum(["wxpay", "alipay"]);
export type PaymentStatementChannel = z.infer<typeof paymentStatementChannelSchema>;

export const paymentStatementRowStatusSchema = z.enum(["succeeded", "refunded", "closed"]);
export type PaymentStatementRowStatus = z.infer<typeof paymentStatementRowStatusSchema>;

export const paymentStatementRowInputSchema = z.object({
  merchantOrderId: z.string().trim().min(1).max(128),
  providerOrderId: z.string().trim().max(256).nullable(),
  amountMinor: z.number().int().min(0).max(1_000_000_000),
  refundedAmountMinor: z.number().int().min(0).max(1_000_000_000),
  status: paymentStatementRowStatusSchema,
  occurredAt: z.string().datetime(),
}).strict();
export type PaymentStatementRowInput = z.infer<typeof paymentStatementRowInputSchema>;

export const paymentStatementImportInputSchema = z.object({
  channel: paymentStatementChannelSchema,
  fileName: z.string().trim().min(1).max(200),
  checksum: z.string().regex(/^[a-f0-9]{64}$/),
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  rows: z.array(paymentStatementRowInputSchema).min(1).max(5_000),
}).strict().superRefine((value, context) => {
  if (value.dateFrom > value.dateTo) context.addIssue({ code: "custom", path: ["dateTo"], message: "结束日期不能早于开始日期" });
  const seen = new Set<string>();
  value.rows.forEach((row, index) => {
    if (seen.has(row.merchantOrderId)) context.addIssue({ code: "custom", path: ["rows", index, "merchantOrderId"], message: "商户订单号不能重复" });
    seen.add(row.merchantOrderId);
  });
});
export type PaymentStatementImportInput = z.infer<typeof paymentStatementImportInputSchema>;

export const paymentStatementBatchStatusSchema = z.enum(["reconciling", "completed", "attention", "failed"]);
export type PaymentStatementBatchStatus = z.infer<typeof paymentStatementBatchStatusSchema>;

export const paymentStatementBatchSchema = z.object({
  id: z.string().min(1).max(128),
  channel: paymentStatementChannelSchema,
  fileName: z.string().min(1).max(200),
  checksum: z.string().regex(/^[a-f0-9]{64}$/),
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  status: paymentStatementBatchStatusSchema,
  rowCount: z.number().int().nonnegative(),
  matchedCount: z.number().int().nonnegative(),
  mismatchCount: z.number().int().nonnegative(),
  grossAmountMinor: z.number().int().nonnegative(),
  refundedAmountMinor: z.number().int().nonnegative(),
  errorMessage: z.string().max(500).nullable(),
  createdBy: z.string().min(1).max(128),
  createdAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
});
export type PaymentStatementBatch = z.infer<typeof paymentStatementBatchSchema>;

export const paymentReconciliationDifferenceTypeSchema = z.enum([
  "missing_platform",
  "missing_provider",
  "amount_mismatch",
  "refund_mismatch",
  "status_mismatch",
]);
export type PaymentReconciliationDifferenceType = z.infer<typeof paymentReconciliationDifferenceTypeSchema>;

export const paymentReconciliationDifferenceSchema = z.object({
  id: z.string().min(1).max(128),
  batchId: z.string().min(1).max(128),
  channel: paymentStatementChannelSchema,
  type: paymentReconciliationDifferenceTypeSchema,
  merchantOrderId: z.string().max(128).nullable(),
  providerOrderId: z.string().max(256).nullable(),
  platformAmountMinor: z.number().int().nonnegative().nullable(),
  providerAmountMinor: z.number().int().nonnegative().nullable(),
  platformRefundedAmountMinor: z.number().int().nonnegative().nullable(),
  providerRefundedAmountMinor: z.number().int().nonnegative().nullable(),
  platformStatus: z.string().max(32).nullable(),
  providerStatus: paymentStatementRowStatusSchema.nullable(),
  resolutionStatus: z.enum(["open", "in_review", "resolved", "ignored"]),
  assignee: z.string().max(64).nullable(),
  resolutionAction: z.enum(["none", "platform_fix", "provider_fix", "accepted"]),
  resolutionNote: z.string().max(500).nullable(),
  resolvedAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime(),
  createdAt: z.string().datetime(),
});
export type PaymentReconciliationDifference = z.infer<typeof paymentReconciliationDifferenceSchema>;

export const paymentStatementJobStatusSchema = z.enum(["queued", "running", "completed", "failed", "skipped"]);
export const paymentStatementJobSchema = z.object({
  id: z.string().min(1).max(128),
  configId: z.string().min(1).max(128),
  configName: z.string().min(1).max(64),
  channel: paymentStatementChannelSchema,
  statementDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  trigger: z.enum(["manual", "scheduled"]),
  status: paymentStatementJobStatusSchema,
  attemptCount: z.number().int().nonnegative(),
  batchId: z.string().max(128).nullable(),
  errorMessage: z.string().max(500).nullable(),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  createdBy: z.string().min(1).max(128),
  createdAt: z.string().datetime(),
});
export type PaymentStatementJob = z.infer<typeof paymentStatementJobSchema>;

export const paymentDifferenceAuditSchema = z.object({
  id: z.string().min(1).max(128),
  differenceId: z.string().min(1).max(128),
  action: z.enum(["created", "status_changed", "assigned", "noted"]),
  fromStatus: z.enum(["open", "in_review", "resolved", "ignored"]).nullable(),
  toStatus: z.enum(["open", "in_review", "resolved", "ignored"]),
  assignee: z.string().max(64).nullable(),
  resolutionAction: z.enum(["none", "platform_fix", "provider_fix", "accepted"]),
  note: z.string().max(500).nullable(),
  operatorId: z.string().min(1).max(128),
  createdAt: z.string().datetime(),
});
export type PaymentDifferenceAudit = z.infer<typeof paymentDifferenceAuditSchema>;

export const updatePaymentDifferenceInputSchema = z.object({
  resolutionStatus: z.enum(["open", "in_review", "resolved", "ignored"]),
  assignee: z.string().trim().min(1).max(64).nullable(),
  resolutionAction: z.enum(["none", "platform_fix", "provider_fix", "accepted"]),
  note: z.string().trim().max(500),
}).strict().superRefine((value, context) => {
  if ((value.resolutionStatus === "resolved" || value.resolutionStatus === "ignored") && !value.note) {
    context.addIssue({ code: "custom", path: ["note"], message: "完成或忽略差异时必须填写处理说明" });
  }
});
export type UpdatePaymentDifferenceInput = z.infer<typeof updatePaymentDifferenceInputSchema>;

export const triggerPaymentStatementJobInputSchema = z.object({
  configId: z.string().trim().min(1).max(128),
  statementDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
}).strict();
export type TriggerPaymentStatementJobInput = z.infer<typeof triggerPaymentStatementJobInputSchema>;

export const paymentStatementWorkspaceSchema = z.object({
  batches: z.array(paymentStatementBatchSchema),
  differences: z.array(paymentReconciliationDifferenceSchema),
  jobs: z.array(paymentStatementJobSchema).default([]),
  audits: z.array(paymentDifferenceAuditSchema).default([]),
  metrics: z.object({
    totalDifferences: z.number().int().nonnegative(),
    resolvedDifferences: z.number().int().nonnegative(),
    overdueDifferences: z.number().int().nonnegative(),
    completionRate: z.number().min(0).max(100),
  }).default({ totalDifferences: 0, resolvedDifferences: 0, overdueDifferences: 0, completionRate: 100 }),
});
export type PaymentStatementWorkspace = z.infer<typeof paymentStatementWorkspaceSchema>;

export const paymentWebhookChannelSchema = z.enum(["wxpay", "alipay"]);
export type PaymentWebhookChannel = z.infer<typeof paymentWebhookChannelSchema>;

export const paymentWebhookEventTypeSchema = z.enum(["payment", "refund"]);
export type PaymentWebhookEventType = z.infer<typeof paymentWebhookEventTypeSchema>;

export const paymentWebhookProcessingStatusSchema = z.enum(["received", "processed", "ignored", "rejected", "failed"]);
export type PaymentWebhookProcessingStatus = z.infer<typeof paymentWebhookProcessingStatusSchema>;

export const paymentWebhookEventSchema = z.object({
  id: z.string().min(1).max(128),
  eventKey: z.string().min(8).max(200),
  channel: paymentWebhookChannelSchema,
  eventType: paymentWebhookEventTypeSchema,
  source: z.enum(["callback", "query", "retry"]),
  providerEventId: z.string().max(256).nullable(),
  orderId: z.string().max(128).nullable(),
  refundId: z.string().max(128).nullable(),
  outTradeNo: z.string().max(128).nullable(),
  outRefundNo: z.string().max(128).nullable(),
  providerOrderId: z.string().max(256).nullable(),
  tradeState: z.string().max(64),
  normalizedState: z.enum(["pending", "processing", "succeeded", "failed", "canceled", "refunded"]),
  amountMinor: z.number().int().nonnegative().nullable(),
  currency: z.string().regex(/^[A-Z]{3}$/).nullable(),
  signatureStatus: z.enum(["verified", "rejected"]),
  signatureSerial: z.string().max(256).nullable(),
  payloadDigest: z.string().regex(/^[a-f0-9]{64}$/),
  processingStatus: paymentWebhookProcessingStatusSchema,
  failureCode: z.string().max(128).nullable(),
  failureReason: z.string().max(500).nullable(),
  duplicateCount: z.number().int().nonnegative(),
  attemptCount: z.number().int().positive(),
  requestTimestamp: z.string().datetime().nullable(),
  occurredAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  processedAt: z.string().datetime().nullable(),
});
export type PaymentWebhookEvent = z.infer<typeof paymentWebhookEventSchema>;

export const paymentWebhookEventAuditSchema = z.object({
  id: z.string().min(1).max(128),
  eventId: z.string().min(1).max(128),
  action: z.enum(["received", "duplicate", "signature_rejected", "processed", "ignored", "rejected", "failed", "retried"]),
  fromStatus: paymentWebhookProcessingStatusSchema.nullable(),
  toStatus: paymentWebhookProcessingStatusSchema,
  reason: z.string().max(500).nullable(),
  operatorId: z.string().min(1).max(128),
  createdAt: z.string().datetime(),
});
export type PaymentWebhookEventAudit = z.infer<typeof paymentWebhookEventAuditSchema>;

export const paymentWebhookEventListQuerySchema = z.object({
  keyword: z.string().trim().max(100).default(""),
  channel: z.union([paymentWebhookChannelSchema, z.literal("all")]).default("all"),
  eventType: z.union([paymentWebhookEventTypeSchema, z.literal("all")]).default("all"),
  status: z.union([paymentWebhookProcessingStatusSchema, z.literal("all")]).default("all"),
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
}).strict().superRefine((value, context) => {
  if (value.dateFrom > value.dateTo) context.addIssue({ code: "custom", path: ["dateTo"], message: "结束日期不能早于开始日期" });
});
export type PaymentWebhookEventListQuery = z.infer<typeof paymentWebhookEventListQuerySchema>;

export const paymentWebhookEventListResultSchema = z.object({
  items: z.array(paymentWebhookEventSchema),
  total: z.number().int().nonnegative(),
  summary: z.object({
    total: z.number().int().nonnegative(),
    processed: z.number().int().nonnegative(),
    attention: z.number().int().nonnegative(),
    rejected: z.number().int().nonnegative(),
    duplicates: z.number().int().nonnegative(),
  }),
});
export type PaymentWebhookEventListResult = z.infer<typeof paymentWebhookEventListResultSchema>;

export const paymentWebhookEventDetailSchema = z.object({
  event: paymentWebhookEventSchema,
  audits: z.array(paymentWebhookEventAuditSchema),
});
export type PaymentWebhookEventDetail = z.infer<typeof paymentWebhookEventDetailSchema>;

export const nativePaymentStatusSchema = z.enum(["creating", "ready"]);
export type NativePaymentStatus = z.infer<typeof nativePaymentStatusSchema>;

export const nativePaymentSchema = z.object({
  orderId: z.string().min(1).max(128),
  status: nativePaymentStatusSchema,
  codeUrl: z.string().url().nullable(),
  expiresAt: z.string().datetime().nullable(),
});
export type NativePayment = z.infer<typeof nativePaymentSchema>;

export const wechatJsapiPaymentParamsSchema = z.object({
  appId: z.string().regex(/^wx[a-fA-F0-9]{16}$/),
  timeStamp: z.string().regex(/^[0-9]{10}$/),
  nonceStr: z.string().min(8).max(64),
  package: z.string().regex(/^prepay_id=[A-Za-z0-9_-]{10,64}$/),
  signType: z.literal("RSA"),
  paySign: z.string().min(16).max(512),
}).strict();
export type WechatJsapiPaymentParams = z.infer<typeof wechatJsapiPaymentParamsSchema>;

export const createWechatJsapiPaymentInputSchema = z.object({
  code: z.string().trim().regex(/^[A-Za-z0-9_-]{1,256}$/),
}).strict();
export type CreateWechatJsapiPaymentInput = z.infer<typeof createWechatJsapiPaymentInputSchema>;

export const wechatJsapiPaymentSchema = z.object({
  orderId: z.string().min(1).max(128),
  status: nativePaymentStatusSchema,
  expiresAt: z.string().datetime().nullable(),
  payment: wechatJsapiPaymentParamsSchema.nullable(),
});
export type WechatJsapiPayment = z.infer<typeof wechatJsapiPaymentSchema>;

export const wechatJsapiTestOrder = {
  amountMinor: 100,
  currency: "CNY",
  description: "实体商品支付测试",
} as const;

export const createWechatJsapiTestInputSchema = z.object({
  code: z.string().trim().regex(/^[A-Za-z0-9_-]{1,256}$/),
  idempotencyKey: z.string().trim().min(8).max(128),
}).strict();
export type CreateWechatJsapiTestInput = z.infer<typeof createWechatJsapiTestInputSchema>;

export const wechatJsapiTestResultSchema = wechatJsapiPaymentSchema.extend({
  order: paymentOrderSchema,
});
export type WechatJsapiTestResult = z.infer<typeof wechatJsapiTestResultSchema>;

export const transitionPaymentOrderInputSchema = z.object({
  // Application clients may advance payment processing, but a refund must be
  // confirmed by a trusted provider workflow rather than client-controlled
  // state transitions.
  status: z.enum(["pending", "processing", "succeeded", "failed", "canceled"]),
  version: z.number().int().positive(),
  providerOrderId: z.string().trim().max(256).optional(),
  failureCode: z.string().trim().max(128).optional(),
  failureReason: z.string().trim().max(500).optional(),
}).strict().superRefine((value, context) => {
  if (value.status === "failed" && (!value.failureReason || value.failureReason.length < 1)) {
    context.addIssue({ code: "custom", path: ["failureReason"], message: "失败状态必须提供原因" });
  }
});
export type TransitionPaymentOrderInput = z.infer<typeof transitionPaymentOrderInputSchema>;
